import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { GetFoodOrderingSettings } from "api/rpc/2024-04/facilityAdmin/facility/foodOrdering";
import { GetNewsLetterPosts } from "api/rpc/2024-04/facilityAdmin/facility/newsletter";
import { GetLoyaltyProgram } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import Input from "components/form/input";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showSuccess } from "redux/actions/ui";

import "./facilityLinks.scss";

function isFoodOrderingEnabled(settings: unknown) {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return false;
  }

  return !!(settings as { enabled?: boolean }).enabled;
}

type TLinkField = {
  id: string;
  label?: string;
  href: string;
  ready: boolean;
  loading?: boolean;
  setupPath: string;
};

type TLinkSection = {
  id: string;
  title: string;
  description: string;
  /** Hidden when the facility module for this link is turned off. */
  visible: boolean;
  fields: TLinkField[];
};

export default function FacilityLinks() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const facility = facilityStore.facility;
  const clientShortName = facility?.client?.short_name || facility?.client_short_name || "";
  const facilityShortName = facility?.short_name || "";
  const namesReady = Boolean(clientShortName && facilityShortName);

  const [foodOrderingEnabled, setFoodOrderingEnabled] = useState<boolean | null>(null);
  const [newsletterPublished, setNewsletterPublished] = useState<boolean | null>(null);
  const [loyaltyReady, setLoyaltyReady] = useState<boolean | null>(null);

  useEffect(() => {
    if (!facility?.module_food_ordering) {
      setFoodOrderingEnabled(false);
      return;
    }

    setFoodOrderingEnabled(null);
    let cancelled = false;

    void (async () => {
      const res = await GetFoodOrderingSettings(null, false);
      if (cancelled) {
        return;
      }

      setFoodOrderingEnabled(isFoodOrderingEnabled(res.status === StatusCode.OK ? res.data : null));
    })();

    return () => {
      cancelled = true;
    };
  }, [facility?.id, facility?.module_food_ordering]);

  useEffect(() => {
    if (!facility?.id) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const res = await GetNewsLetterPosts({ limit: 100 }, false);
      if (cancelled) {
        return;
      }

      if (res.status !== StatusCode.OK || !Array.isArray(res.data)) {
        setNewsletterPublished(true);
        return;
      }

      setNewsletterPublished(res.data.some(post => post.status === "published"));
    })();

    return () => {
      cancelled = true;
    };
  }, [facility?.id]);

  useEffect(() => {
    if (!facility?.module_loyalty_points) {
      setLoyaltyReady(false);
      return;
    }

    if (facilityStore.loyaltyProgram?.id) {
      setLoyaltyReady(true);
      return;
    }

    setLoyaltyReady(null);
    let cancelled = false;

    void (async () => {
      const res = await GetLoyaltyProgram(false);
      if (cancelled) {
        return;
      }

      const program = res.status === StatusCode.OK ? res.data : null;
      const programId =
        program && typeof program === "object" && !Array.isArray(program) && "id" in program
          ? (program as { id?: number }).id
          : undefined;
      setLoyaltyReady(!!programId);
    })();

    return () => {
      cancelled = true;
    };
  }, [facility?.module_loyalty_points, facilityStore.loyaltyProgram?.id]);

  async function copyLink(href: string) {
    await navigator.clipboard.writeText(href);
    dispatch(showSuccess(t("Copied link to clipboard")));
  }

  const portalBase = `${window.origin}/portal/${clientShortName}`;
  const teeTimeUrl = `${portalBase}/teetimes/${facilityShortName}`;
  const newsletterUrl = `${portalBase}/newsletter/${facilityShortName}`;
  const teeTimeLoading = !!facility?.module_tee_sheet && !facilityStore.teeSheetSettings;
  const teeTimeReady = !!facility?.module_tee_sheet && !!facilityStore.teeSheetSettings?.enable_web_booking;
  const foodOrderingLoading = !!facility?.module_food_ordering && foodOrderingEnabled === null;
  const marketingPermissions = facility?.permissions;
  const marketingReady =
    !!marketingPermissions?.email_marketing_allow_opt_ins &&
    (!!marketingPermissions?.email_marketing_sign_up_button || !!marketingPermissions?.email_marketing_booking_page_checkbox);

  const sections: TLinkSection[] = [
    {
      id: "customer-login",
      title: t("Customer login"),
      description: t("Customers sign in to their account from this link."),
      visible: true,
      fields: [
        {
          id: "customer-login",
          href: `${window.origin}/tee-on/portal/login`,
          ready: true,
          setupPath: "",
        },
      ],
    },
    {
      id: "tee-time",
      title: t("Book a tee time online"),
      description: t("Customers use this link to book a tee time online."),
      visible: !!facility?.module_tee_sheet,
      fields: [
        {
          id: "tee-time",
          href: teeTimeUrl,
          ready: namesReady && teeTimeReady,
          loading: !namesReady || teeTimeLoading,
          setupPath: "/admin/settings/tee-sheet/general",
        },
      ],
    },
    {
      id: "online-store",
      title: t("Online store"),
      description: t("Customers shop products from this link."),
      visible: true,
      fields: [
        {
          id: "online-store",
          href: `${window.origin}/online-store/${facilityShortName}`,
          ready: namesReady,
          loading: !namesReady,
          setupPath: "/admin/product",
        },
      ],
    },
    {
      id: "events",
      title: t("Events"),
      description: t("Customers browse leagues, tournaments, and hospitality events."),
      visible: true,
      fields: [
        {
          id: "events",
          href: `${portalBase}/events/${facilityShortName}`,
          ready: namesReady,
          loading: !namesReady,
          setupPath: "/admin/events",
        },
      ],
    },
    {
      id: "calendar",
      title: t("Calendar"),
      description: t("Customers view the public events calendar."),
      visible: true,
      fields: [
        {
          id: "calendar",
          href: `${portalBase}/calendar/${facilityShortName}`,
          ready: namesReady,
          loading: !namesReady,
          setupPath: "/admin/events-calendar",
        },
      ],
    },
    {
      id: "reservations",
      title: t("Activities"),
      description: t("Customers book an activity from this link."),
      visible: !!facility?.module_reservations,
      fields: [
        {
          id: "reservations",
          href: `${portalBase}/reservations/${facilityShortName}`,
          ready: namesReady,
          loading: !namesReady,
          setupPath: "/admin/settings/reservations/modules",
        },
      ],
    },
    {
      id: "food-ordering",
      title: t("Food Ordering"),
      description: t("Customers order food from this link."),
      visible: !!facility?.module_food_ordering,
      fields: [
        {
          id: "food-ordering",
          href: `${portalBase}/food-ordering/menu/${facilityShortName}`,
          ready: namesReady && foodOrderingEnabled === true,
          loading: !namesReady || foodOrderingLoading,
          setupPath: "/admin/food-ordering/overview",
        },
      ],
    },
    {
      id: "newsletter",
      title: t("Newsletter"),
      description: t("Share the newsletter and the page where customers can opt in."),
      visible: true,
      fields: [
        {
          id: "newsletter-link",
          label: t("Newsletter link"),
          href: newsletterUrl,
          ready: namesReady && newsletterPublished === true,
          loading: !namesReady || newsletterPublished === null,
          setupPath: "/admin/newsletter",
        },
        {
          id: "newsletter-opt-in",
          label: t("Opt-in link"),
          href: newsletterUrl,
          ready: namesReady,
          loading: !namesReady,
          setupPath: "/admin/newsletter",
        },
      ],
    },
    {
      id: "marketing",
      title: t("Marketing opt-in"),
      description: t("Customers opt in to marketing emails from the booking page."),
      visible: true,
      fields: [
        {
          id: "marketing",
          href: teeTimeUrl,
          ready: namesReady && marketingReady,
          loading: !namesReady,
          setupPath: "/admin/email-marketing/settings",
        },
      ],
    },
    {
      id: "loyalty",
      title: t("Loyalty points opt in"),
      description: t("Customers sign in and join the loyalty program from this link."),
      visible: !!facility?.module_loyalty_points,
      fields: [
        {
          id: "loyalty",
          href: `${window.origin}/tee-on/portal/loyalty-program`,
          ready: loyaltyReady === true,
          loading: loyaltyReady === null,
          setupPath: "/admin/settings/loyalty-program",
        },
      ],
    },
  ];

  const visibleSections = sections.filter(section => section.visible);

  return (
    <Page
      title={t("Links")}
      subtitle={t("Customer links for this facility. Copy a link to share it, or set up anything that is not ready.")}
    >
      <Card>
        {visibleSections.map(section => (
          <Card.Section key={section.id}>
            <div className="facility-links-item">
              <div>
                <p className="facility-links-title">{section.title}</p>
                <p className="facility-links-description">{section.description}</p>
              </div>
              <div className="facility-links-fields">
                {section.fields.map(field => (
                  <LinkField
                    key={field.id}
                    field={field}
                    showLabel={section.fields.length > 1}
                    onCopy={copyLink}
                    onSetup={path => history.push(path)}
                  />
                ))}
              </div>
            </div>
          </Card.Section>
        ))}
      </Card>
    </Page>
  );
}

function LinkField(props: {
  field: TLinkField;
  showLabel: boolean;
  onCopy: (href: string) => Promise<void>;
  onSetup: (path: string) => void;
}) {
  const { field, showLabel, onCopy, onSetup } = props;
  const { t } = useTranslation();

  if (field.loading) {
    return <p className="facility-links-status">{t("Loading...")}</p>;
  }

  if (!field.ready) {
    return (
      <div className="facility-links-setup">
        <p className="facility-links-setup-copy">
          {showLabel ? t("{{label}} is not set up yet.", { label: field.label }) : t("Not set up yet.")}
        </p>
        <Button type="secondary" size="medium" onClick={() => onSetup(field.setupPath)}>
          {t("Set up")}
        </Button>
      </div>
    );
  }

  return (
    <Input
      id={field.id}
      label={showLabel ? field.label : undefined}
      labelHidden={!showLabel}
      disabled
      value={field.href}
      trailingButtons={[
        <Button key="copy" onClick={() => void onCopy(field.href)}>
          <FontAwesomeIcon icon={["far", "copy"]} style={{ marginRight: "5px" }} />
          {t("Copy")}
        </Button>,
      ]}
    />
  );
}
